<template>
  <div class="max-w-3xl mx-auto p-6">
    <div
      class="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 flex justify-between items-center bg-gradient-to-r from-teal-500 to-emerald-500"
      >
        <h2 class="text-xl font-semibold text-white tracking-tight">
          {{ $t("editProduct.title") }}
        </h2>
      </div>

      <!-- Form Content -->
      <div class="p-6">
        <form class="space-y-5">
          <!-- Name -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-gray-700">{{
              $t("editProduct.name")
            }}</label>
            <input
              v-model="form.name"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              type="text"
            />
          </div>

          <!-- Images -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-gray-700">{{
              $t("editProduct.images")
            }}</label>
            <div class="grid grid-cols-2 gap-4">
              <div
                v-for="(img, index) in form.image"
                :key="index"
                class="space-y-2"
              >
                <img
                  :src="
                    isEdited(index) ? previewImages[index] : fullImageUrl(img)
                  "
                  class="rounded-lg shadow h-32 w-full object-cover border border-gray-200"
                />
                <div class="relative">
                  <input
                    type="file"
                    @change="handleImageChange($event, index)"
                    class="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-gray-700">{{
              $t("editProduct.description")
            }}</label>
            <textarea
              v-model="form.description"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
            ></textarea>
          </div>

          <!-- Price -->
          <div class="space-y-1">
            <label class="block text-sm font-medium text-gray-700">{{
              $t("editProduct.price")
            }}</label>
            <input
              v-model="form.price"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              type="number"
            />
          </div>

          <!-- Country, State, Area Selection -->
          <div class="grid grid-cols-1 md:grid-cols-1 gap-5">
            <!-- Country -->
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.country") }}
                <span class="text-red-500">*</span>
              </label>
              <select
                v-model="form.country_id"
                @change="onCountryChange"
                required
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ $t("products.addModal.selectCountry") }}
                </option>
                <option
                  v-for="country in countries"
                  :key="country.id"
                  :value="country.id"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- State -->
            <div class="space-y-1" v-if="form.country_id">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.state") }}
                <span class="text-red-500">*</span>
              </label>
              <select
                v-model="form.state_id"
                @change="onStateChange"
                required
                :disabled="loadingStates"
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ loadingStates ? 'Loading...' : $t("products.addModal.selectState") }}
                </option>
                <option
                  v-for="state in states"
                  :key="state.id"
                  :value="state.id"
                >
                  {{ state.name }}
                </option>
              </select>
            </div>

            <!-- Area -->
            <div v-if="areas.length > 0" class="space-y-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.area") }}
                <span class="text-red-500">*</span>
              </label>
              <select
                v-model="form.area_id"
                required
                :disabled="loadingAreas"
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ loadingAreas ? 'Loading...' : $t("products.addModal.selectArea") }}
                </option>
                <option v-for="area in areas" :key="area.id" :value="area.id">
                  {{ area.name }}
                </option>
              </select>
            </div>
          </div>



          <!-- Location Section -->
          <div class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">{{
              $t("editProduct.location")
            }}</label>

            <div class="flex flex-col sm:flex-row gap-3">
              <div class="flex-1">
                <label class="block text-xs text-gray-500 mb-1"
                  >Search Location</label
                >
                <div class="flex rounded-lg shadow-sm">
                  <input
                    v-model="searchQuery"
                    @keyup.enter="searchLocation"
                    type="text"
                    placeholder="Enter location (e.g. Amman, Jordan)"
                    class="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                  />
                  <button
                    type="button"
                    @click="searchLocation"
                    class="px-4 bg-teal-500 text-white rounded-r-lg hover:bg-teal-600 transition-colors flex items-center justify-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <button
                type="button"
                @click="useMyLocation"
                class="px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-teal-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span class="text-sm">My Location</span>
              </button>
            </div>

            <div
              ref="mapContainer"
              class="h-64 w-full rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              style="min-height: 256px"
            ></div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs text-gray-500 mb-1">Latitude</label>
                <div
                  class="flex items-center px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span class="text-sm text-gray-700">{{
                    form.latitude || "Not set"
                  }}</span>
                </div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1"
                  >Longitude</label
                >
                <div
                  class="flex items-center px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span class="text-sm text-gray-700">{{
                    form.longitude || "Not set"
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Dynamic Fields -->
          <div v-for="field in form.fields" :key="field.id" class="space-y-1">
            <label class="block text-sm font-medium text-gray-700">{{
              field.label
            }}</label>

            <!-- SELECT -->
            <select
              v-if="field.type === 'select'"
              v-model="field.value"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
            >
              <option disabled value="">Select {{ field.label }}</option>
              <option
                v-for="(opt, idx) in field.options"
                :key="idx"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
<!-- COLOR -->
<select
  v-else-if="field.type === 'color'"
  v-model="field.value"
  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
>
  <option disabled value="">{{ $t("editProduct.selectColor") }}</option>
  <option
    v-for="(color, i) in colorOptions"
    :key="i"
    :value="color"
  >
    {{ color }}
  </option>
</select>
            <!-- RADIO -->
            <div v-else-if="field.type === 'radio'" class="space-y-1">
              <div
                v-for="(opt, idx) in field.options"
                :key="idx"
                class="flex items-center gap-2"
              >
                <input
                  type="radio"
                  :name="'radio_' + field.id"
                  :value="opt"
                  v-model="field.value"
                  class="text-teal-500"
                />
                <label class="text-sm text-gray-700">{{ opt }}</label>
              </div>
            </div>

            <!-- CHECKBOX -->
            <div v-else-if="field.type === 'checkbox'" class="space-y-1">
              <div
                v-for="(opt, idx) in field.options"
                :key="idx"
                class="flex items-center gap-2"
              >
                <input
                  type="checkbox"
                  :id="`checkbox_${field.id}_${idx}`"
                  :value="opt"
                  v-model="field.value"
                  class="text-teal-500"
                />
                <label
                  :for="`checkbox_${field.id}_${idx}`"
                  class="text-sm text-gray-700"
                  >{{ opt }}</label
                >
              </div>
            </div>

            <!-- TEXT / NUMBER -->
            <input
              v-else
              v-model="field.value"
              :type="field.type === 'number' ? 'number' : 'text'"
              class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
            />
          </div>

          <!-- Submit Button -->
          <div class="pt-2">
            <button
              type="button"
              @click="saveChanges"
              class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all transform hover:scale-[1.01]"
            >
              {{ $t("editProduct.submit") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet's default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "EditProductPage",
  data() {
    return {
      form: {
        name: "",
        image: [],
        description: "",
        price: "",
        currency_code: "",
        currency_id: "",
        country_id: "",
        state_id: "",
        area_id: "",
        fields: [],
        latitude: null,
        longitude: null,
      },
      countries: [],
      states: [],
      areas: [],
      loadingStates: false,
      loadingAreas: false,
      editedImages: {},
      previewImages: {},
      searchQuery: "",
      map: null,
      marker: null,
      colorOptionsEn: [
      'White', 'Red', 'Green', 'Blue', 'Light Blue',
      'Cement', 'Black', 'Yellow', 'Petrol', 'Orange',
      'Bronze', 'Purple', 'Brown', 'Beige', 'Tan',
      'Turquoise', 'Maroon', 'Gold', 'Grey', 'Pink',
      'Olive', 'Silver', 'Navy'
    ],
    colorOptionsAr: [
      'أبيض', 'أحمر', 'أخضر', 'أزرق', 'أزرق فاتح',
      'أسمنتي', 'أسود', 'أصفر', 'بترولي', 'برتقالي',
      'برونزي', 'بنفسجي', 'بني', 'بيج', 'تان',
      'تركواز', 'خمري', 'ذهبي', 'رمادي', 'زهري',
      'زيتي', 'فضي', 'كحلي'
    ],
    };
  },
  computed: {
  colorOptions() {
    return this.$i18n.locale === 'ar'
      ? this.colorOptionsAr
      : this.colorOptionsEn;
  }
},
  async mounted() {
    await Promise.all([
      this.fetchCountries(),
      this.fetchItem()
    ]);
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    async fetchItem() {
      const itemId = this.$route.params.id || 7;
      try {
        const res = await api.get(`/v1/items/${itemId}`);

        // ✅ Parse checkbox fields before assigning to form
        res.data.fields = res.data.fields.map((field) => {
          if (field.type === "checkbox") {
            try {
              // Try to parse as JSON (["a", "b"])
              field.value = JSON.parse(field.value);
            } catch {
              // Or fallback to splitting by comma if stored as "a,b"
              field.value =
                typeof field.value === "string" ? field.value.split(",") : [];
            }
          }
          return field;
        });

        this.form = res.data;

        // Pre-populate states and areas when editing existing item
        if (this.form.country_id) {
          await this.fetchStates(this.form.country_id);
          if (this.form.state_id) {
            await this.onStateChange();
          }
        }

        // ✅ Initialize map with item location if exists
        if (this.form.latitude && this.form.longitude) {
          this.$nextTick(() => {
            this.setMapLocation(this.form.latitude, this.form.longitude);
          });
        }
      } catch (err) {
        console.error("Failed to load item", err);
      }
    },
    fullImageUrl(path) {
      return `https://admin.from-malk.com/storage/${path}`;
    },

    async fetchCountries() {
      try {
        const lang = localStorage.getItem("lang_code") || "ar";
        const res = await api.get("/v1/countries", { params: { lang } });
        this.countries = res.data;
      } catch (err) {
        console.error("Failed to load countries", err);
      }
    },

    async fetchCurrencies() {
      // Currency is always SAR - no need to fetch from API
      console.log("Currency is hardcoded to SAR - skipping API call");
      this.currencies = [];
      this.form.currency_id = null; // Will always use SAR currency code
    },

    async onCountryChange() {
      this.states = [];
      this.form.state_id = "";
      this.areas = [];
      this.form.area_id = "";
      if (this.form.country_id) {
        await this.fetchStates(this.form.country_id);
      }
    },

    async fetchStates(countryId) {
      try {
        this.loadingStates = true;
        const lang = localStorage.getItem("lang_code") || "ar";
        const res = await api.get("/v1/states", { params: { country_id: countryId, lang } });
        this.states = res.data;
      } catch (err) {
        console.error("Failed to load states", err);
      } finally {
        this.loadingStates = false;
      }
    },

    async onStateChange() {
      try {
        this.loadingAreas = true;
        this.areas = [];
        this.form.area_id = "";
        const res = await api.get(`/v1/states/${this.form.state_id}/areas`);
        this.areas = res.data;
      } catch (err) {
        console.error("Failed to load areas", err);
      } finally {
        this.loadingAreas = false;
      }
    },

    isEdited(index) {
      return !!this.previewImages[index];
    },

    handleImageChange(event, index) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImages[index] = e.target.result;
      };
      reader.readAsDataURL(file);

      this.editedImages[index] = file;
    },

    initMap() {
      // Ensure map container exists
      if (!this.$refs.mapContainer) {
        console.error("Map container not found");
        return;
      }

      // Remove existing map if it exists
      if (this.map) {
        this.map.remove();
      }

      // Create new map with default view if no coordinates exist
      const defaultLat = 31.9454; // Default to Amman, Jordan
      const defaultLng = 35.9284;
      const defaultZoom = this.form.latitude ? 13 : 8;

      this.map = L.map(this.$refs.mapContainer).setView(
        [this.form.latitude || defaultLat, this.form.longitude || defaultLng],
        defaultZoom
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map);

      // Add click handler
      this.map.on("click", (e) => {
        this.updateLocation(e.latlng.lat, e.latlng.lng);
      });

      // Add marker if coordinates exist
      if (this.form.latitude && this.form.longitude) {
        this.addMarker(this.form.latitude, this.form.longitude);
      }
    },

    addMarker(lat, lng) {
      if (this.marker) {
        this.map.removeLayer(this.marker);
      }
      this.marker = L.marker([lat, lng]).addTo(this.map);
    },

    setMapLocation(lat, lng) {
      if (!this.map) return;
      this.map.setView([lat, lng], 13);
      this.addMarker(lat, lng);
    },

    updateLocation(lat, lng) {
      this.form.latitude = lat;
      this.form.longitude = lng;
      this.setMapLocation(lat, lng);
    },

    async searchLocation() {
      if (!this.searchQuery) return;

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            this.searchQuery
          )}`
        );
        const results = await res.json();
        if (results.length > 0) {
          const lat = parseFloat(results[0].lat);
          const lon = parseFloat(results[0].lon);
          this.updateLocation(lat, lon);
        } else {
          alert("Location not found.");
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      }
    },

    useMyLocation() {
      if (!navigator.geolocation) {
        alert("Geolocation not supported by your browser");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.updateLocation(lat, lon);
        },
        (err) => {
          console.error("Geolocation error:", err);
          alert("Unable to retrieve your location");
        },
        { timeout: 10000 }
      );
    },

    async saveChanges() {
      console.log("EditProductPage - Form data before submission:");
      console.log("currency: SAR (hardcoded)");
      console.log("country_id:", this.form.country_id);
      console.log("state_id:", this.form.state_id);
      console.log("area_id:", this.form.area_id);
      
      const formData = new FormData();
      formData.append("title", this.form.name);
      formData.append("description", this.form.description);
      formData.append("price", this.form.price);
      
      // Always use SAR currency
      formData.append("currency_code", "SAR");
      console.log("Using hardcoded currency: SAR");
      
      formData.append("country_id", this.form.country_id);
      formData.append("state_id", this.form.state_id);
      formData.append("area_id", this.form.area_id);
      formData.append("latitude", this.form.latitude);
      formData.append("longitude", this.form.longitude);

      this.form.fields.forEach((field, i) => {
        formData.append(`fields[${i}][id]`, field.id);
        if (Array.isArray(field.value)) {
          // checkbox: send array as JSON string
          formData.append(`fields[${i}][value]`, JSON.stringify(field.value));
        } else {
          formData.append(`fields[${i}][value]`, field.value);
        }
      });

      const updatedImages = [...this.form.image];

      for (const index in this.editedImages) {
        const file = this.editedImages[index];
        formData.append(`images[${index}]`, file);
        updatedImages[index] = `__UPDATED__${index}`;
      }

      formData.append("existing_images", JSON.stringify(this.form.image));
      formData.append("image_order", JSON.stringify(updatedImages));

      try {
        const id = this.$route.params.id;
        await api.post(`/v1/items/${id}/update`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Product updated successfully!");
        this.$router.push("/userProfile"); // Redirect after success
      } catch (error) {
        console.error("Update error:", error);
        alert("Failed to update product. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
img {
  max-width: 100%;
  height: auto;
}

.leaflet-container {
  height: 100%;
  width: 100%;
  z-index: 0;
  background-color: #f8fafc;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Smooth transitions for interactive elements */
button,
input,
select,
.map-container {
  transition: all 0.2s ease;
}
</style>
